import { EXTENSION_ID } from '@/constants/app-constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInterval } from '@/hooks/use-interval';
import { usePrevious } from 'react-use';
import { isEqual } from 'lodash-es';
import { useRetry } from './use-retry';

export const useCheckExtensionInstalled = ({ callback = () => {} } = {}) => {
  const errResponse = {
    installed: true,
    tasks: { auto_contact: 0, manual_count: 0 },
  };
  const [response, setResponse] = useState(errResponse);
  const prevExtensionResponse = usePrevious(response);

  /**
  	Kula extension is only suppored in chromium browser. For other browsers like Safari amd Mozilla,
  	We consider extension installed as failed and we don't show outreach and extension-dependent sections
	 */

  const checkExtension = useCallback(() => {
    if (
      (import.meta.env.VITE_ENV === 'staging' &&
        window.location.hostname === 'e2e.kulastg.com') ||
      (import.meta.env.VITE_ENV === 'development' &&
        import.meta.env.VITE_TEST === 'true')
    ) {
      setResponse({ installed: true });
      return;
    }
    const errResponse = {
      installed: false,
    };
    if (
      typeof window?.chrome === 'undefined' ||
      !window?.chrome?.runtime?.sendMessage
    ) {
      if (isEqual(errResponse, prevExtensionResponse)) {
        return;
      }
      setResponse(errResponse);
      callback(errResponse);
      return;
    }
    window?.chrome?.runtime?.sendMessage(
      EXTENSION_ID,
      { checkInstall: 'install' },
      function (result) {
        if (window?.chrome?.runtime?.lastError || !result) {
          if (isEqual(errResponse, prevExtensionResponse)) {
            return;
          }
          setResponse(errResponse);
          callback(errResponse);
          return;
        }
        const { response } = result;
        if (isEqual(response, prevExtensionResponse)) {
          return;
        }
        setResponse(response);
        callback(response);
        return;
      }
    );
  }, [callback]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    checkExtension();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useInterval(
    () => {
      checkExtension();
    },
    import.meta.env.VITE_ENV === 'development' ? 3000000 : 3000
  );

  return response;
};

export const useSendMessageToExtension = () => {
  const sendMessage = useCallback(
    ({ messageTypeobj = {}, callback = () => {} }) => {
      if (
        typeof window?.chrome === 'undefined' ||
        !window?.chrome?.runtime?.sendMessage
      ) {
        return;
      }
      window?.chrome?.runtime?.sendMessage(
        EXTENSION_ID,
        messageTypeobj,
        callback
      );
    },
    []
  );

  return { sendMessage };
};

export const useEnrichViaExtension = () => {
  const ref = useRef(null);
  const { sendMessage } = useSendMessageToExtension();
  const [retry, setretry] = useState(false);
  const [resolveFn, setResolveFn] = useState(() => () => {});

  const checkExtRes = async () => {
    return new Promise(async (resolve, reject) => {
      try {
        if (ref?.current === null || ref?.current === undefined) {
          reject({ status: 'failed' });
          return;
        }
        resolve(ref?.current);
      } catch {}
    });
  };

  const { inProgress, data, error } = useRetry({
    fn: checkExtRes,
    args: {},
    retryOptions: { retries: 4, minTimeout: 2000, factor: 1 },
    start: retry,
  });

  useEffect(() => {
    if (inProgress) return;
    if (data) {
      setretry(false);
      resolveFn(data);
      return;
    }
    if (error) {
      setretry(false);
      resolveFn(error);
    }
  }, [inProgress, data, error]); //eslint-disable-line react-hooks/exhaustive-deps

  const enrichViaExtension = async ({ lid = '' }) => {
    ref.current = null;
    setretry(true);
    sendMessage({
      messageTypeobj: { type: 'enrich', lid },
      callback: function (result) {
        if (window?.chrome?.runtime?.lastError || !result) {
          ref.current = null;
        }
        if (result) {
          const { response = {} } = result;
          ref.current = response;
        }
        return;
      },
    });
    return new Promise((resolve) => {
      setResolveFn(() => resolve);
    });
  };

  return { enrichViaExtension };
};
